


import React, { useState, useEffect } from "react";
import { ChevronLeft, Upload, X, CheckCircle, AlertCircle } from "lucide-react";
import medical from '../../Assets/Images/rewamp/medical.svg';

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`rounded-full px-6 py-2 font-medium transition-all duration-200 
      ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : variant === "outline"
          ? "border border-gray-400 text-textGray hover:bg-lightGray"
          : "text-White"
      } ${className}`}
    style={
      !disabled && variant !== "outline"
        ? { backgroundColor: '#42BA85' }
        : {}
    }
  >
    {children}
  </button>
);

// Toast Component - FIXED with proper colors
const Toast = ({ message, type, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === "success" ? "#E8F5E9" : "#FFE8E8";
  const borderColor = type === "success" ? "#4CAF50" : "#FF6B6B";
  const textColor = type === "success" ? "#2E7D32" : "#FF6B6B";
  const Icon = type === "success" ? CheckCircle : AlertCircle;

  return (
    <div 
      className="fixed top-4 right-4 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg animate-fade-in z-50"
      style={{ 
        backgroundColor: bgColor, 
        borderColor: borderColor,
        borderWidth: '2px'
      }}
    >
      <Icon className="h-5 w-5" style={{ color: textColor }} />
      <p className="text-sm font-medium" style={{ color: textColor }}>
        {message}
      </p>
      <button onClick={onClose} className="ml-2">
        <X className="h-4 w-4" style={{ color: textColor }} />
      </button>
    </div>
  );
};

export default function MedicalReports({ patientInfo, onNext, onPrevious, patientData = {} }) {
  const [selectedDisease, setSelectedDisease] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [toast, setToast] = useState(null);
  const [apiLabReport, setApiLabReport] = useState(null);

  const diseaseOptions = [
    "None",
    "Diabetes",
    "Hypertension",
    "Thyroid",
    "PCOD/PCOS",
    "Heart Disease",
    "Other"
  ];
  useEffect(() => {
  if (patientData) {
    if (patientData.diseases) {
      setSelectedDisease(patientData.diseases);
    }
    if (patientData.additional_info) {
      setAdditionalDetails(patientData.additional_info);
    }
    if (patientData.pdf_report) {
      setPdfBase64(patientData.pdf_report);
    }
  }
}, [patientData]);

  useEffect(() => {
    const convertApiFileToBase64 = async (source) => {
      try {
        // If it's already base64 (starts with data URI or looks encoded)
        if (source.startsWith("data:application/pdf") || /^[A-Za-z0-9+/=]+$/.test(source.slice(0, 50))) {
          setApiLabReport(source.includes(",") ? source.split(",")[1] : source);
          console.log("✅ API file already in Base64 format");
          return;
        }

        // Otherwise, treat as URL and fetch
        const response = await fetch(source);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(",")[1];
          setApiLabReport(base64);
          console.log("✅ API file converted to Base64 successfully");
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("❌ Failed to convert API file to Base64:", error);
      }
    };

    if (patientInfo?.LABORDERDOC) {
      convertApiFileToBase64(patientInfo.LABORDERDOC);
    }
  }, [patientInfo]);

  const showToast = (message, type = "success") => setToast({ message, type });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processFile = async (file) => {
    if (file.type !== "application/pdf") {
      showToast("Please upload a PDF file only", "error");
      return;
    }
    try {
      const base64 = await convertFileToBase64(file);
      setUploadedFile(file);
      setPdfBase64(base64);
      setApiLabReport(null);
      showToast(`${file.name} uploaded successfully!`, "success");
    } catch {
      showToast("Failed to read file", "error");
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files?.[0]) await processFile(e.target.files[0]);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) await processFile(e.dataTransfer.files[0]);
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPdfBase64("");
  };

  const handleRemoveApiFile = () => {
    setApiLabReport(null);
  };

  const handleViewApiFile = () => {
    if (apiLabReport) {
      const pdfWindow = window.open();
      pdfWindow.document.write(
        `<iframe width='100%' height='100%' src='data:application/pdf;base64,${apiLabReport}'></iframe>`
      );
    } else if (patientInfo?.LABORDERDOC?.startsWith("http")) {
      window.open(patientInfo.LABORDERDOC, "_blank");
    }
  };

  const getApiFileName = (filePath) => {
    if (!filePath) return "Lab Report.pdf";
    try {
      if (filePath.startsWith("http")) {
        const parts = filePath.split("/");
        return parts[parts.length - 1] || "Lab Report.pdf";
      }
      return "Lab_Report.pdf";
    } catch {
      return "Lab_Report.pdf";
    }
  };

  const handleSubmit = async () => {
    let finalBase64 = "";

    if (uploadedFile) {
      finalBase64 = await convertFileToBase64(uploadedFile);
    } else if (apiLabReport) {
      finalBase64 = apiLabReport;
    }

    const formData = {
      ...patientData,
      diseases: selectedDisease,
      additional_info: additionalDetails,
      pdf_report: finalBase64 || "",
    };

    console.log("✅ Form submitted — file converted to Base64:", finalBase64?.slice(0, 100) + "...");
    onNext(formData);
  };
  const handlePrevious = async () => {
  // Save current selections before going back
  let finalBase64 = "";

  if (uploadedFile) {
    finalBase64 = await convertFileToBase64(uploadedFile);
  } else if (apiLabReport) {
    finalBase64 = apiLabReport;
  }

  const formData = {
    diseases: selectedDisease,
    additional_info: additionalDetails,
    pdf_report: finalBase64 || "",
  };

  console.log("✅ MedicalReports saving before previous:", formData);
  onPrevious(formData);
};

 return (
  <div 
    className="py-2 sm:py-3 px-2 sm:px-4"
    style={{
      backgroundColor: '#fff',
        backgroundImage: 'var(--section-bg-gradient)'
    }}
  >
    {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

    <div className="w-full max-w-full mx-auto">
      {/* Header */}
      <div className="mb-5 sm:mb-6 flex items-center gap-3 sm:gap-4 px-2 sm:px-4">
  
  {/* ICON (replaced) */}
  <div className="rounded-xl bg-[#EAF3FA] p-2.5 flex items-center justify-center shrink-0">
    <img 
      src={medical} 
      alt="Basic Info" 
      className="h-12 w-12 sm:h-14 sm:w-14 object-contain"

    />
  </div>

  {/* TEXT */}
  <div className="flex-1 min-w-0">
    <h2 
      className="text-lg sm:text-2xl font-bold break-words"
      style={{ color: "var(--color-brand-primary)" }}
    >
      Medical Records & History
    </h2>

    <p className="text-sm sm:text-base text-textGray mt-1 break-words">
      Please provide any relevant medical conditions, lab reports, or additional details that can help us tailor your diet plan effectively.
    </p>

  </div>
</div>

      {/* Form */}
      <div className="px-0 sm:px-2 lg:px-4 space-y-4 sm:space-y-5">
        {/* Disease */}
        <div>
          <label className="block text-sm sm:text-base font-bold text-quantumPrimary mb-3 sm:mb-4">
            Do you have any existing medical condition?
          </label>
          <select
            value={selectedDisease}
            onChange={(e) => setSelectedDisease(e.target.value)}
            className="w-full border-2 border-gray-300 rounded-lg p-3 sm:p-4 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent"
          >
            <option value="">Select Disease</option>
            {diseaseOptions.map((d) => (
              <option key={d}>{d}</option>
            ))}
          </select>
        </div>

        {/* Additional Details */}
        <div> 
          <label className="block text-sm sm:text-base font-bold text-quantumPrimary mb-3 sm:mb-4">
            Any additional details you'd like us to consider?
          </label>
          <textarea
            value={additionalDetails}
            onChange={(e) => {
              const text = e.target.value;
              const words = text.trim().split(/\s+/).filter(word => word.length > 0);

              if (words.length <= 150) {
                setAdditionalDetails(text);
              }
            }}
            placeholder="Type here"
            className="w-full border-2 border-gray-300 rounded-lg p-3 sm:p-4 min-h-[120px] sm:min-h-[150px] text-sm sm:text-base focus:outline-none focus:ring-2 resize-none"
          />

          <div className="flex justify-between items-center mt-2"> 
            <p className="text-xs text-textGray">
              {additionalDetails.trim().split(/\s+/).filter(word => word.length > 0).length} / 150 words
            </p>
            {additionalDetails.trim().split(/\s+/).filter(word => word.length > 0).length >= 150 && (
              <p className="text-xs text-red-500 font-medium">Word limit reached</p>
            )} 
          </div>
        </div>

        {/* Lab Report */}
        <div>
          <label className="block text-sm sm:text-base font-bold text-quantumPrimary mb-3 sm:mb-4">
            Lab Report (PDF only)
          </label>

          {/* API or Preuploaded Report */}
          {apiLabReport && !uploadedFile && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-lightGreen border-2 border-secondaryColor rounded-lg p-4 sm:p-5 mb-6 gap-4">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-lg text-White" style={{ backgroundColor: '#42BA85' }}>
                  <span className="text-xs font-bold">PDF</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm sm:text-base font-semibold text-quantumPrimary truncate">
                    {getApiFileName(patientInfo?.LABORDERDOC)}
                  </p>
                  <p className="text-xs sm:text-sm text-textGray">
                    (From your medical records)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={handleViewApiFile}
                  className="transition-colors px-3 sm:px-4 py-2 border border-secondaryColor rounded-lg font-medium text-xs sm:text-sm flex-1 sm:flex-none"
                  style={{ color: '#00CFA5' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  View
                </button>

                <label htmlFor="replace-api" className="cursor-pointer flex-1 sm:flex-none">
                  <button
                    type="button"
                    onClick={() => document.getElementById("replace-api").click()}
                    className="transition-colors px-3 sm:px-4 py-2 border border-secondaryColor rounded-lg font-medium text-xs sm:text-sm w-full"
                    style={{ color: '#00CFA5' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    Replace
                  </button>
                  <input
                    id="replace-api"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </label>

                <button
                  onClick={handleRemoveApiFile}
                  className="transition-colors p-2"
                  style={{ color: '#FF6B6B' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>
          )}

          {/* Upload Section */}
          {!apiLabReport && (
            <>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 sm:p-12 text-center cursor-pointer transition-all ${
                  dragActive
                    ? "border-secondaryColor bg-lightGreen"
                    : "border-gray-300 bg-lightGray hover:border-gray-400 hover:bg-gray-100"
                }`}
              >
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileInput}
                  className="hidden"
                  id="file-input"
                />
                <label htmlFor="file-input" className="cursor-pointer block">
                  <Upload className="mx-auto mb-3 sm:mb-4 h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                  <p className="text-sm sm:text-base text-quantumPrimary font-medium">
                    Drag & drop a PDF file here or click to select
                  </p>
                  <p className="text-xs sm:text-sm text-textGray mt-2">Maximum file size: 10 MB</p>
                </label>
              </div>

              {uploadedFile && (
                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between bg-lightGreen border-2 border-secondaryColor rounded-lg p-4 sm:p-5 animate-fade-in gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-lg text-White" style={{ backgroundColor: '#00CFA5' }}>
                      <span className="text-xs font-bold">PDF</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-semibold text-quantumPrimary truncate">
                        {uploadedFile.name}
                      </p>
                      <p className="text-xs sm:text-sm text-textGray">
                        {(uploadedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="transition-colors p-2 self-end sm:self-auto"
                    style={{ color: '#FF6B6B' }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    <X className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0 mt-8 sm:mt-12 lg:mt-16 pt-6 sm:pt-8 border-t border-gray-200">
          <button
            onClick={handlePrevious}
            className="flex items-center justify-center gap-2 rounded-full border-2 border-[#99B3C8] bg-White px-6 sm:px-8 py-2.5 sm:py-3 font-semibold text-[#004276] transition hover:bg-[#F1F8FD] text-sm sm:text-base"
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            Previous
          </button>
          <button
  onClick={handleSubmit}
  className="rounded-full px-8 sm:px-10 py-2.5 sm:py-3 font-semibold text-white shadow-lg transition text-sm sm:text-base hover:opacity-90"
  style={{ backgroundColor: 'var(--color-brand-primary)' }}
>
  Done
</button>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes fade-in {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in { animation: fade-in 0.3s ease-out; }
    `}</style>
  </div>
);
}